<template>
	<div class="orders-container">
		<LayoutBox class="orders">
			<div class="orders__header">
				<AppDropdown
					class="orders__dropdown"
					:options="['All', 'For Processing', 'For Delivery', 'Cancelled', 'Received']"
					v-model="filters.statusPlaceholder"
					@input="handleStatus"
				>
					Filter By
				</AppDropdown>

				<AppDropdown
					class="orders__dropdown"
					:options="['Latest', 'Oldest']"
					v-model="filters.sort"
					@input="changeSort"
				>
					Sort By
				</AppDropdown>

				<AppButton
					v-if="selected.length"
					modifier="small-primary"
					:disabled="!selectedIds.length"
					@click="downloadExcel"
				>
					Download
				</AppButton>

				<AppButton
					v-if="forProcessingOnly"
					modifier="small-secondary"
					@click="changeStatus('delivery')"
				>
					Deliver
				</AppButton>

				<AppButton
					v-if="forDeliveryOnly"
					modifier="small-secondary"
					@click="changeStatus('received')"
				>
					Receive
				</AppButton>
				<AppButton
					v-if="forProcessingOnly"
					modifier="small-danger"
					@click="changeStatus('cancelled')"
				>
					Cancel
				</AppButton>

				<AppTextField
					class="orders__search"
					icon-right="search"
					placeholder="Search"
					v-model="filters.searchKeyword"
					@input="search"
				/>
			</div>

			<AppTable
				class="orders__table"
				:fields="fields"
				:dataset="orderList.data"
				:selected="selected"
				@select="selected=[...$event]"
                @click="viewOrder"
				:loading="orderListLoading"
			>
				<template #customer_name="{ props: { item } }">
					<span :class="{ 'summarize': item.customer_name.length > 80 }">
						{{ item.customer_name | summarize(80) }}
					</span>
					<span v-if="item.customer_name.length > 80" class="full-text">
						{{ item.customer_name }}
					</span>
				</template>

				<template #address="{ props: { item } }">
					<span :class="{ 'summarize': item.address.length > 80 }">
						{{ item.address | summarize(80) }}
					</span>
					<span v-if="item.address.length > 80" class="full-text">
						{{ item.address }}
					</span>
				</template>

                <template #order_items="{ props: { item } }">
					<span class="items-wrapper" @mouseover="hoverNumber = item.id" @mouseleave="hoverNumber = ''">
						{{ item.order_items }}
					</span>
                    <div class="items-container" v-if="hoverNumber === item.id">
                        <div class="header">
                            <p class="item">Items</p>
                            <p class="qty">Qty</p>
                        </div>
                        <div class="item-box" v-for="(x, index) in Object.keys(item.item_breakdown)" :key="index">
                            <div class="box" v-if="x !== 'strap'">
                                <p class="item">{{ x }}</p>
                                <p class="qty">{{ item.item_breakdown[x] }}</p>
                            </div>

                            <div class="box" v-if="x === 'strap' && item.item_breakdown[x]">
                                <div class="type">
                                    <p>Band Strap</p>
                                    <span class="color">
                                        {{ displayColor(item.item_breakdown[x]) }}
                                    </span>
                                </div>
                                <p class="qty">{{ strapQty(item.item_breakdown[x]) }}</p>
                            </div>
                        </div>
                    </div>
				</template>

				<template #loading>
					<AppLoading/>
				</template>
			</AppTable>

			<div class="orders__pagination">
				<p>
					{{`${orderList.from || ''}-${orderList.to || ''}`}}
				</p>

				<AppCountPicker
					placeholder="Item"
					min="10"
					max="60"
					step="10"
					v-model="filters.itemsPerPage"
					:disabled="orderListLoading"
					@change="getOrderList(true)"
				/>

				<AppPagination
					v-model="filters.page"
					:total="orderList.last_page"
					:disabled="orderListLoading"
					@change="getOrderList()"
				/>
			</div>
		</LayoutBox>
	</div>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import * as orderModule from '@/store/order'
import OrderFilter from '@/class/OrderFilter'

export default {
	data () {
		return {
			hoverNumber: '',
			filters: {
				page: 1,
				itemsPerPage: 10,
				searchKeyword: '',
				status: '',
				sort: 'Latest',
				statusPlaceholder: 'All'
			},

			fields: [
				{ name: 'transaction_id', text: 'ID' },
				{ name: 'order_date', text: 'Order Date' },
				{ name: 'customer_name', text: 'Customer Name' },
				{ name: 'address', text: 'Address' },
				{ name: 'country', text: 'Country' },
				// { name: 'email', text: 'Email' },
				// { name: 'mobile', text: 'Contact' },
				{ name: 'order_type', text: 'Order Type', style: 'text-transform: capitalize;' },
				{ name: 'order_items', text: 'Items' },
				{ name: 'quantity', text: 'Qty', type: 'number' },
				{ name: 'total_amount', text: 'Total', type: 'number' },
				// { name: 'code', text: 'Code' },
				// { name: 'shipment', text: 'Shipment' },
				{ name: 'status', text: 'Status' }
			]
		}
	},

	beforeCreate () {
		if (!this.$store._modulesNamespaceMap['order/']) {
			this.$store.registerModule('order', orderModule.default)
		}
	},

	beforeRouteLeave (to, from, next) {
		this.$store.dispatch('order/clearData')
		next()
	},

	async created () {
		this.$router.push(`orders${this.lastQueryParams}`)
		await this.checkQuery()
		await this.getOrderList()
	},

	computed: {
		...mapFields('order', {
			selected: 'selected',
			orderList: 'orderList',
			orderListLoading: 'orderListLoading',
			lastQueryParams: 'lastQueryParams'
		}),

		forProcessingOnly () {
			return (this.selected && this.selected.length > 0 && this.selected.every(i => i.status.toLowerCase() === 'for processing'))
		},

		forDeliveryOnly () {
			return (this.selected && this.selected.length > 0 && this.selected.every(i => i.status.toLowerCase() === 'for delivery'))
		},

		selectedIds () {
			return this.selected.map(item => item.id)
		}
	},

	methods: {
		downloadExcel () {
			this.selected.forEach(item => {
				const form = new FormData()
			    form.append('transaction_id', item.transaction_id)
				this.$store.dispatch('order/downloadExcel', { form, name: item.customer_name })
			})
		},

		changeStatus (status) {
			const {
				page,
				itemsPerPage,
				searchKeyword,
				sort
			} = this.filters
			const form = new FormData()
			form.append('id', this.selectedIds)
			form.append('action', status)
			form.append('page', page)

			if (itemsPerPage) {
				form.append('count', itemsPerPage)
			}

			if (searchKeyword) {
				form.append('keyword', searchKeyword)
			}

			if (sort) {
				form.append('sort', sort)
			}

			if (this.filters.status) {
				form.append('status', this.filters.status)
			}

			const payload = {
				form,
				status,
				store: this.$store
			}

			this.$store.dispatch('order/actions', payload)
		},

		checkQuery () {
			const {
				page,
				keyword,
				status,
				sort_by
			} = this.$route.query

			if (page) {
				this.filters.page = parseInt(page)
			}

			if (keyword) {
				this.filters.searchKeyword = keyword
			}

			if (status) {
				const _status = `${status.charAt(0).toUpperCase()}${status.slice(1)}`
                console.log(_status)
				if (['Processing', 'Delivery'].includes(_status)) {
					this.filters.status = _status.toLowerCase()
					this.filters.statusPlaceholder = `For ${_status}`
				} else {
					this.filters.status = _status
					this.filters.statusPlaceholder = _status
					// this.filters.searchKeyword = _status
				}
			}

			if (sort_by) {
				this.filters.sort = sort_by
			}
		},

		async getOrderList (resetPage = false) {
			if (resetPage) this.filters.page = 1
			this.selected = []
			const { apiQuery, routerQuery } = await new OrderFilter(this.filters)
			this.$router.push({ query: routerQuery })
			this.$store.dispatch('order/getOrderList', apiQuery)
		},

		handleStatus (value) {
			if (value) {
				if (value === 'All') {
					this.filters.status = ''
				} else {
					this.filters.statusPlaceholder = value
					this.filters.status = value.replace('For ', '').toLowerCase()
				}
			} else {
				this.filters.statusPlaceholder = 'All'
				this.filters.status = ''
			}

			this.filters.page = 1

			this.selected = []
			this.getOrderList()
		},

		changeSort (e) {
			if (!e) this.filters.sort = 'Latest'
			this.filters.page = 1
			this.selected = []
			this.getOrderList()
		},

		search (e) {
			clearTimeout(this.timeout)
			this.timeout = setTimeout(() => {
				this.filters.page = 1
				this.selected = []
				this.getOrderList()
			}, 500)
		},

		viewOrder ({ transaction_id }) {
			this.lastQueryParams = this.$route.fullPath.replace('/orders', '')
			this.$router.push(`/order/${transaction_id}`)
		},

		displayColor (strap) {
			let arr = []
			for (let key in strap) {
				arr.push(`${key} (${strap[key]})`)
			}
			return arr.join(', ')
		},

		strapQty (strap) {
			let sum = 0
			for (let key in strap) {
				sum += strap[key]
			}
			return sum
		}
	}
}
</script>

<style lang="scss" scoped>
.orders-container {
	height: 100%;
	padding: 1.5em;
}

.orders {
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;

	&__header {
		width: 100%;
		display: flex;
		align-items: flex-end;
		margin-bottom: 10px;

		> *:not(:last-child) {
			margin-right: 10px;
		}
	}

	&__search {
		margin-left: auto;
		width: 200px;
	}

	&__dropdown { width: 150px; }

	&__table {
		flex: 1 1 auto;
		margin-bottom: 10px
	}

	&__pagination {
		width: 100%;
		display: flex;
		align-items: center;

		> *:first-child { margin-right: auto; }
		> *:not(:first-child) { margin-left: 20px; }
	}

    .items-wrapper {
        margin-bottom: 15px;
    }

    .items-container {
        position: absolute;
        z-index: 2;
        padding: 10px;
        box-shadow: 0px 3px 6px #24242430;
        border-radius: 3px;
        background-color: #fff;
        width: 180px;

        .header {
            display: flex;
            margin-bottom: 10px;

            p {
                font-weight: 600;

                &.qty {
                    margin-left: auto;
                }
            }
        }

        .item-box {
            display: flex;
            margin-bottom: 10px;

            &:last-child {
                margin-bottom: 0px;
            }

            .item {
                text-transform: capitalize;
            }

            .qty {
                margin-left: auto;
            }

            .box {
                display: flex;
                width: 100%;
                align-items: center;

                .type {
                    width: 120px;
                }

                .color {
                    color: #2DC4B6;
                    text-transform: capitalize;
                }
            }

        }
    }
}
</style>
